import React from 'react';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import { connect } from 'react-redux';
// Add these imports at the top
import {
  Button,
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import axiosInstance from 'utils/axiosUtil';
import { showConfirmationDialog } from 'store/actions/systemActions';
import moment from 'moment';

const styles = (theme) => ({
  formWrapper: {
    padding: theme.spacing(3),
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    textAlign: 'right',
    color: '#666',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 500,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
  },
  infoText: {
    fontSize: 14,
    color: '#666',
    marginBottom: theme.spacing(0.5),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 500,
    color: theme.palette.primary.main,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  codesContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  codesInput: {
    flex: 1,
    position: 'relative',
  },
  codesTextArea: {
    '& .MuiInputBase-root': {
      fontFamily: 'monospace',
      fontSize: 14,
    },
  },

  deleteButton: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  nameTextField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  loadingSpinner: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  invalidCodeText: {
    color: theme.palette.error.main,
    fontStyle: 'italic',
  },
  validationMessage: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
    fontSize: 14,
  },
  codesTableContainer: {
    flex: 1,
    maxHeight: '400px',
  },
  invalidRow: {
    backgroundColor: '#ffebee',
  },
  invalidCodeText: {
    color: theme.palette.error.main,
    fontStyle: 'italic',
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
  },  
  codesTableContainer: {
    flex: 1,
    maxHeight: '400px',
  },
  invalidRow: {
    backgroundColor: '#ffebee !important',
  },
  invalidCodeText: {
    color: theme.palette.error.main,
    fontStyle: 'italic',
  },
  tableHeaderCell: {
    fontWeight: 600,
    backgroundColor: '#f5f5f5',
  },
  tableRowEven: {
    backgroundColor: '#fafafa',
  },
  tableRowOdd: {
    backgroundColor: '#ffffff',
  },  
  disclaimerContent: {
      color: '#9E9E9E',
      fontStyle: 'italic',
      fontSize: 13,
      marginTop: 16,
      textAlign: 'center',
    },  
});

class CustomServiceLineDialog extends React.Component {
  constructor(props) {
    super(props);
    
    const { serviceLine } = props;
    
    this.state = {
      name: serviceLine?.name || '',
      codeType: serviceLine?.is_diagnosis ? 'dx' : 'px',
      codesInput: '',
      codes: [],
      isLoading: false,
      isSaving: false,
      isLookingUp: false,
      lookupTimeout: null,
      invalidCodes: [],
    };
  }

  componentDidMount() {
    if (this.props.serviceLine) {
      this.fetchCodes();
    }
  }

  componentWillUnmount() {
    if (this.state.lookupTimeout) {
      clearTimeout(this.state.lookupTimeout);
    }
  }

  fetchCodes = async () => {
    const { serviceLine, clientId } = this.props;
    
    try {
      this.setState({ isLoading: true });
      
      const response = await axiosInstance.get('/ServicelinesToCodes', {
        params: {
          selected_client_id: clientId,
          opts: {
            where: {
              serviceline_id: serviceLine.id,
            },
            include: [
              {
                association: 'codes',
                attributes: ['id', 'code', 'code_type', 'description'],
                required: true,
              },
            ],
            order: [['code_id', 'ASC']],
          },
        },
      });

      const records = response.data || [];
      
      const codes = records.map(record => ({
        code: record.codes?.code || '',
        type: this.state.codeType,
        description: record.codes?.description || '',
        isValid: true,
      }));
      
      const codesInput = codes.map(c => c.code).join(', ');
      
      this.setState({
        codes,
        codesInput,
        isLoading: false,
      });
    } catch (error) {
      console.error('Error fetching codes:', error);
      this.setState({ isLoading: false });
    }
  };

  getDiagnosisFromType = (typeString) => {
    return typeString === 'dx';
  };

  lookupCode = async (code) => {
    const { clientId } = this.props;
    const { codeType } = this.state;
    const isDiagnosis = this.getDiagnosisFromType(codeType);
    
    // Code type mapping:
    // Diagnoses (dx): code_type in (2, 3) - ICD-10-CM
    // Procedures (px): code_type in (1, 4) - HCPCS & ICD-10-PCS
    const codeTypes = isDiagnosis ? [2, 3] : [1, 4];

    try {
      const response = await axiosInstance.get('/Codes', {
        params: {
          selected_client_id: clientId,
          opts: JSON.stringify({
            where: {
              code: code.trim(),
              code_type: {
                $in: codeTypes,
              },
            },
            attributes: ['id', 'code', 'code_type', 'description'],
            limit: 2,
          }),
        },
      });

      const results = response.data || [];
      
      if (results.length === 1) {
        return {
          code: results[0].code,
          description: results[0].description || '',
          isValid: true,
        };
      } else if (results.length === 0) {
        return {
          code: code.trim(),
          description: '',
          isValid: false,
        };
      } else {
        return {
          code: code.trim(),
          description: '',
          isValid: true,
        };
      }
    } catch (error) {
      console.error('Error looking up code:', error);
      return {
        code: code.trim(),
        description: '',
        isValid: false,
      };
    }
  };

  parseCodes = (input) => {
    if (!input || !input.trim()) return [];
    
    // Normalize newlines to commas, then split
    const normalizedInput = input.replace(/[\r\n]+/g, ',');
    
    const codeArray = normalizedInput
      .split(',')
      .map(code => code.trim())
      .filter(code => code.length > 0);
    
    // Remove duplicates by using a Set
    const uniqueCodes = [...new Set(codeArray)];
    
    return uniqueCodes.map(code => ({
      code,
      type: this.state.codeType,
      setting: 'Inpatient',
      description: '',
      isValid: null,
    }));
  };
 

  lookupCodesDebounced = (input) => {
    const { lookupTimeout } = this.state;
    
    if (lookupTimeout) {
      clearTimeout(lookupTimeout);
    }

    const codes = this.parseCodes(input);
    this.setState({ codes });

    const newTimeout = setTimeout(async () => {
      this.setState({ isLookingUp: true });
      
      const lookedUpCodes = await Promise.all(
        codes.map(async (code) => {
          const result = await this.lookupCode(code.code);
          return {
            ...code,
            description: result.description,
            isValid: result.isValid,
          };
        })
      );

      this.setState({
        codes: lookedUpCodes,
        isLookingUp: false,
      });
    }, 500);

    this.setState({ lookupTimeout: newTimeout });
  };

  validateAllCodes = async () => {
    const { codes } = this.state;
    
    this.setState({ isLookingUp: true });
    
    const validatedCodes = await Promise.all(
      codes.map(async (code) => {
        if (code.isValid === null || code.isValid === false) {
          const result = await this.lookupCode(code.code);
          return {
            ...code,
            description: result.description,
            isValid: result.isValid,
          };
        }
        return code;
      })
    );

    const invalidCodes = validatedCodes
      .filter(code => !code.isValid)
      .map(code => code.code);

    this.setState({
      codes: validatedCodes,
      invalidCodes,
      isLookingUp: false,
    });

    return invalidCodes.length === 0;
  };

  onCodesInputChange = (e) => {
    const codesInput = e.target.value;
    
    this.setState({ codesInput, invalidCodes: [] });
    this.lookupCodesDebounced(codesInput);
  };

  onCodeTypeChange = (e) => {
    const codeType = e.target.value;
    const codes = this.parseCodes(this.state.codesInput);
    
    this.setState({
      codeType,
      codes,
      invalidCodes: [],
    });

    this.lookupCodesDebounced(this.state.codesInput);
  };

  validateName = (name, codeType) => {
    if (!name || !name.trim()) {
      return name; // Don't modify empty names
    }

    const trimmedName = name.trim();
    const suffix = codeType === 'dx' ? ' Dx' : ' Px';
    
    // Check if name already ends with the correct suffix (case-insensitive)
    const upperName = trimmedName.toUpperCase();
    if (upperName.endsWith(' DX') || upperName.endsWith(' PX')) {
      // Remove existing suffix and add the correct one
      const nameWithoutSuffix = trimmedName.slice(0, -3).trim();
      return nameWithoutSuffix + suffix;
    }
    
    // Add the suffix if it doesn't exist
    return trimmedName + suffix;
  };

onSave = async () => {
  const { serviceLine, clientId, userId, onSave } = this.props;
  const { name, codeType, codes } = this.state;

  if (!name.trim()) {
    alert('Please enter a name for the custom service line');
    return;
  }

  if (codes.length === 0) {
    alert('Please enter at least one code');
    return;
  }

  const allValid = await this.validateAllCodes();
  
  if (!allValid) {
    alert('Some codes are invalid. Please review the highlighted codes and correct them before saving.');
    return;
  }

  try {
    this.setState({ isSaving: true });

    const isDiagnosis = this.getDiagnosisFromType(codeType);
    
    // Validate and auto-correct the name with DX/PX suffix
    const validatedName = this.validateName(name, codeType);

    // Get code strings
    const codeStrings = codes.map(c => c.code);

    // Prepare the service line data
    const serviceLineData = {
      name: validatedName,
      client_id: clientId,
      created_by: serviceLine ? serviceLine.created_by : userId,
      last_edited_by: userId,
      last_edited: new Date(),
      is_diagnosis: isDiagnosis,
      codes: codeStrings, // Pass codes array
    };

    let serviceLineId;

    if (serviceLine) {
      serviceLineId = serviceLine.id;
      serviceLineData.id = serviceLineId;
      serviceLineData.opts = {
        where: { id: serviceLineId }
      };
    } else {
      serviceLineData.opts = {};
    }
    
    // Upsert the service line with codes
    const slResponse = await axiosInstance.put('/ClaimsServicelines', serviceLineData, {
      params: {
        selected_client_id: clientId,
      },
    });
    
    serviceLineId = slResponse.data.id;

    // Check if codes changed (or new serviceline)
    const originalCodes = serviceLine?.codes?.map(c => c.code).sort() || [];
    const newCodes = [...codeStrings].sort();
    const codesChanged = !serviceLine || JSON.stringify(originalCodes) !== JSON.stringify(newCodes);

    // Only trigger Lambda if codes changed
    if (codesChanged) {
      await axiosInstance.put('/ClaimsServicelines', {
        id: serviceLineId,
        client_id: clientId,
        opts: {
          where: { id: serviceLineId }
        },
        triggerProcessing: true,
      }, {
        params: {
          selected_client_id: clientId,
        },
      });
    }

    this.setState({ isSaving: false });
    onSave();

  } catch (error) {
    console.error('Error saving custom service line:', error);
    alert('Error saving custom service line. Please try again.');
    this.setState({ isSaving: false });
  }
};


  onDelete = () => {
    const { serviceLine, dispatch } = this.props;
    
    dispatch(
      showConfirmationDialog({
        title: 'Delete Custom Service Line',
        content: `Are you sure you want to delete "${serviceLine.name}"? This action cannot be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        onConfirm: this.deleteServiceLine,
      })
    );
  };

  deleteServiceLine = async () => {
    const { serviceLine, clientId, onSave } = this.props;

    try {
      this.setState({ isSaving: true });

    // delete the totals first
    await axiosInstance.delete('/ProviderServicelineClaimTotals', {
      params: {
        selected_client_id: clientId,
        opts: JSON.stringify({
          where: {
            serviceline_id: serviceLine.id,
          },
        }),
      },
    });

    await axiosInstance.delete('/OrganizationServicelineClaimTotals', {
      params: {
        selected_client_id: clientId,
        opts: JSON.stringify({
          where: {
            serviceline_id: serviceLine.id,
          },
        }),
      },
    });

     await axiosInstance.delete('/UpdatingProviderServicelineClaimTotals', {
      params: {
        selected_client_id: clientId,
        opts: JSON.stringify({
          where: {
            serviceline_id: serviceLine.id,
          },
        }),
      },
    });

    await axiosInstance.delete('/UpdatingOrganizationServicelineClaimTotals', {
      params: {
        selected_client_id: clientId,
        opts: JSON.stringify({
          where: {
            serviceline_id: serviceLine.id,
          },
        }),
      },
    });

      // Delete all code associations first
      await axiosInstance.delete('/ServicelinesToCodes', {
        params: {
          selected_client_id: clientId,
          opts: JSON.stringify({
            where: {
              serviceline_id: serviceLine.id,
            },
          }),
        },
      });

      // Delete the service line
      await axiosInstance.delete('/ClaimsServicelines', {
        data: [serviceLine.id],
        params: {
          selected_client_id: clientId,
        },
      });

      this.setState({ isSaving: false });
      onSave();
    } catch (error) {
      console.error('Error deleting custom service line:', error);
      alert('Error deleting custom service line. Please try again.');
      this.setState({ isSaving: false });
    }
  };

  isProcessing = () => {
    const { serviceLine } = this.props;
    return serviceLine?.start_processing_date && !serviceLine?.end_processing_date;
  };

  render() {
    const { open, serviceLine, classes, onClose } = this.props;
    const { name, codeType, codesInput, codes, isLoading, isSaving, isLookingUp, invalidCodes } = this.state;

    const isEditMode = !!serviceLine;

    const isProcessing = this.isProcessing();

    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby='custom-service-line-dialog'
        fullWidth
        maxWidth='lg'
      >
        <DialogContent style={{ minHeight: '80vh' }}>
          <div className={classes.formWrapper}>
            <div className={classes.headerRow}>
              <div className={classes.headerLeft}>
                <Typography className={classes.headerTitle}>
                  Custom Service Line
                </Typography>
              </div>
            </div>

            <TextField
              fullWidth
              label='Name'
              value={name}
              onChange={(e) => this.setState({ name: e.target.value })}
              variant='outlined'
              placeholder='Enter service line name'
              className={classes.nameTextField}
            />

            <Typography className={classes.sectionTitle}>
              Codes
            </Typography>

            <FormControl component='fieldset'>
              <RadioGroup
                row
                value={codeType}
                onChange={this.onCodeTypeChange}
              >
                <FormControlLabel
                  value='dx'
                  control={<Radio color='primary' />}
                  label='Diagnoses (ICD-10-CM)'
                  disabled={isEditMode}
                />
                <FormControlLabel
                  value='px'
                  control={<Radio color='primary' />}
                  label='Procedures (HCPCS & ICD-10-PCS)'
                  disabled={isEditMode}
                />
              </RadioGroup>
            </FormControl>

            <div className={classes.codesContainer}>
                <div className={classes.codesInput}>
                    <TextField
                      fullWidth
                      multiline
                      rows={18}
                      value={codesInput}
                      onChange={this.onCodesInputChange}
                      variant='outlined'
                      className={classes.codesTextArea}
                    />
                    <Typography className={classes.disclaimerContent}>
                      Provide all codes separated by commas or new lines
                    </Typography>
                    {isLookingUp && (
                      <CircularProgress size={24} className={classes.loadingSpinner} />
                    )}
                    {isLookingUp && (
                    <CircularProgress size={24} className={classes.loadingSpinner} />
                    )}
                    {invalidCodes.length > 0 && (
                    <Typography className={classes.validationMessage}>
                        Invalid codes: {invalidCodes.join(', ')}
                    </Typography>
                    )}
                </div>

            <TableContainer component={Paper} className={classes.codesTableContainer}>
            <Table stickyHeader size="small">
                <TableHead>
                <TableRow>
                    <TableCell className={classes.tableHeaderCell}>Code</TableCell>
                    <TableCell className={classes.tableHeaderCell}>Description</TableCell>
                </TableRow>
                </TableHead>
                <TableBody>
                {codes.length === 0 ? (
                    <TableRow>
                    <TableCell colSpan={2} align="center" style={{ fontStyle: 'italic', color: '#999' }}>
                        No codes added yet
                    </TableCell>
                    </TableRow>
                ) : (
                    codes.map((code, index) => (
                    <TableRow 
                        key={index}
                        className={
                        code.isValid === false 
                            ? classes.invalidRow 
                            : index % 2 === 0 
                            ? classes.tableRowEven 
                            : classes.tableRowOdd
                        }
                    >
                        <TableCell>{code.code}</TableCell>
                        <TableCell>
                        {code.isValid === false ? (
                            <span className={classes.invalidCodeText}>Invalid Code</span>
                        ) : (
                            code.description
                        )}
                        </TableCell>
                    </TableRow>
                    ))
                )}
                </TableBody>
            </Table>
            </TableContainer>
            </div>
          </div>          
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
           
          {serviceLine && (
            <Button
              onClick={this.onDelete}
              className={classes.deleteButton}
              disabled={isSaving}
            >
              Delete
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <Button onClick={onClose} color='primary' disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={this.onSave}
            variant='contained'
            color='primary'
            disabled={isSaving || isLoading || isLookingUp}
          >
            {serviceLine ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

CustomServiceLineDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  serviceLine: PropTypes.object,
  clientId: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {};
}

const styled = withStyles(styles)(CustomServiceLineDialog);
const connected = connect(mapStateToProps)(styled);
export { connected as CustomServiceLineDialog };